/**
 * Comprehensive object and collection utility functions for data manipulation and transformation.
 *
 * This module provides a wide range of helper functions for working with objects, arrays, and collections
 * in a type-safe manner. It includes utilities for:
 *
 * - Object key/value manipulation and transformation
 * - Array processing and filtering
 * - Deep object comparison and equality checking
 * - Data flattening and restructuring
 * - Type-safe mapping and iteration
 * - Collection sorting and organization
 *
 * All functions are designed to work with TypeScript generics for maximum type safety and flexibility.
 *
 * @example
 * ```typescript
 * // Replace object keys recursively
 * replaceKey('oldKey', 'newKey', { oldKey: 1, nested: { oldKey: 2 } })
 * // Result: { newKey: 1, nested: { newKey: 2 } }
 *
 * // Compare objects with optional key filtering
 * isEqualObjects({a: 1, b: 2}, {a: 1, b: 3}, ['a']) // true (only comparing 'a')
 *
 * // Flatten options array to key-value mapping
 * flattenOptionsArray([{label: 'Option A', value: 'a'}])
 * // Result: { 'a': 'Option A' }
 *
 * // Type-safe object iteration
 * each<Config>(configs, (config) => processConfig(config))
 * ```
 */
import { natsort, NatsortOptions } from 'natsort-esm'
import { flatten } from 'flatten-anything'
import { parseStringToNumber, isEqual } from '@utils/misc'
import { isDate } from 'date-fns';
import { compareDate } from '@utils/dates';

/**
 * Recursively replaces a key with a new key throughout an object or array structure.
 *
 * This function traverses the entire object/array structure and replaces all occurrences
 * of the specified old key with the new key. It works with nested objects and arrays,
 * maintaining the original structure while updating key names.
 *
 * @param oldKey - The key to be replaced throughout the structure
 * @param newKey - The new key name to replace the old key
 * @param object - The object or array to process (can be deeply nested)
 * @returns A new object/array with all oldKey occurrences replaced by newKey
 *
 * @example
 * ```typescript
 * // Simple object key replacement
 * replaceKey('id', 'identifier', { id: 1, name: 'test' })
 * // Result: { identifier: 1, name: 'test' }
 *
 * // Nested object replacement
 * replaceKey('old', 'new', {
 *   old: 1,
 *   nested: { old: 2, other: 3 },
 *   array: [{ old: 4 }]
 * })
 * // Result: {
 * //   new: 1,
 * //   nested: { new: 2, other: 3 },
 * //   array: [{ new: 4 }]
 * // }
 * ```
 */
export function replaceKey(oldKey: string, newKey: string, object: any): any {
  if (Array.isArray(object)) {
    return object.map((value) => replaceKey(oldKey, newKey, value));
  }

  if (! isObject(object)) {
    return object;
  }

  for (const key in object) {
    if (key !== oldKey) {
      object[key] = replaceKey(oldKey, newKey, object[key]);
      continue;
    }

    object[newKey] = replaceKey(oldKey, newKey, object[key]);
    delete object[key];
  }

  return object;
}

/**
 * Recursively replaces values for a specific key throughout an object or array structure.
 *
 * This function searches for a specific key and replaces its value when it matches
 * the oldValue. It traverses nested objects and arrays, allowing for selective
 * value replacement based on both key and value criteria.
 *
 * @param object - The object or array to process
 * @param key - The key to search for and replace values
 * @param newValue - The new value to assign when key matches
 * @param oldValue - The value to match against (defaults to true for truthy replacement)
 * @returns A new object/array with matching key-value pairs replaced
 *
 * @example
 * ```typescript
 * // Replace specific values for a key
 * replaceKeyValue({ status: 'active', nested: { status: 'inactive' } }, 'status', 'enabled', 'active')
 * // Result: { status: 'enabled', nested: { status: 'inactive' } }
 *
 * // Replace all truthy values for a key
 * replaceKeyValue({ flag: true, other: { flag: false } }, 'flag', 'yes')
 * // Result: { flag: 'yes', other: { flag: false } }
 * ```
 */
export function replaceKeyValue(object: any, key: string, newValue: any, oldValue: any = true): any {
  if (Array.isArray(object)) {
    return object.map((value) => replaceKeyValue(value, key, newValue, oldValue));
  }

  if (! isObject(object)) {
    return object;
  }

  for (const currentKey in object) {
    if (currentKey !== key) {
      object[currentKey] = replaceKeyValue(object[currentKey], key, newValue, oldValue);
      continue;
    }

    if (oldValue === true) {
      object[key] = newValue;
      continue;
    }

    if (typeof oldValue === 'function') {
      if (oldValue(object[key])) {
        object[key] = newValue;
      }

      continue;
    }

    if (object[key] === oldValue) {
      object[key] = newValue;
      continue;
    }
  }

  return object;
}

/**
 * Replaces the first matching item in an array based on key/value criteria.
 *
 * This function finds the first object in the array where the specified key
 * matches the given value and replaces it with a new value. If no match is found,
 * the original array is returned unchanged.
 *
 * @param array - Array of objects to search and modify
 * @param key - The property key to match against
 * @param value - The value to match for the specified key
 * @param newItem - The new item to replace with
 * @returns New array with the replacement made
 *
 * @example
 * ```typescript
 * const users = [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }];
 * replaceListObject(users, 'id', 1, { id: 1, name: 'Johnny' })
 * // Result: [{ id: 1, name: 'Johnny' }, { id: 2, name: 'Jane' }]
 * ```
 */
export function replaceListObject<T extends SimpleObject<any>>(array: T[], key: string, value: any, newItem: T): T[] {
  return replaceListItems(array, (item) => isObject(item) && (item as SimpleObject<any>)[key] === value, newItem);
}

/**
 * Replaces items in an array using a predicate callback function.
 *
 * This function allows for more complex replacement logic by using a callback
 * to determine which items should be replaced. It's useful when the replacement
 * criteria are more complex than simple key/value matching.
 *
 * @param array - Array to process
 * @param callback - Function that returns true for items to replace
 * @param newItem - The new item to replace matching items with
 * @returns New array with replacements made
 *
 * @example
 * ```typescript
 * const numbers = [1, 2, 3, 4, 5];
 * replaceListItems(numbers, (n) => n > 3, 0)
 * // Result: [1, 2, 3, 0, 0]
 * ```
 */
export function replaceListItems<T>(array: T[], callback: (item: T, index: number) => boolean, newItem: T): T[] {
  for (let i = 0; i < array.length; i++) {
    if (callback(array[i], i)) {
      array[i] = newItem;
    }
  }

  return array;
}

/**
 * Maps a single value or array of values using the same callback function.
 *
 * This function provides a unified interface for mapping operations that can
 * handle both single values and arrays. It's particularly useful when working
 * with data that might be in either format.
 *
 * @param value - Single value or array to map
 * @param callback - Function to apply to each value
 * @returns Mapped value or array of mapped values
 *
 * @example
 * ```typescript
 * // Single value mapping
 * maybeArray(5, (x) => x * 2) // 10
 *
 * // Array mapping
 * maybeArray([1, 2, 3], (x) => x * 2) // [2, 4, 6]
 * ```
 */
export function maybeArray<T = any, R = any>(value: T | readonly T[], callback: (value: T) => R): R | R[] {
  const isArray = Array.isArray(value);

  const array = isArray ? value : [ value ];
  const parsed = array.map(callback);

  return isArray ? parsed : parsed[0];
}

/**
 * Ensures a value is treated as an array, converting single values to single-element arrays.
 *
 * This function provides a safe way to handle values that might be either single items
 * or arrays, normalizing them to always be arrays. It's particularly useful when working
 * with APIs or data that might return either format.
 *
 * @param value - The value to convert to an array
 * @returns An array containing the value(s)
 *
 * @example
 * ```typescript
 * shouldBeArray('single') // ['single']
 * shouldBeArray(['array']) // ['array']
 * shouldBeArray(null) // [null]
 * shouldBeArray(undefined) // [undefined]
 * shouldBeArray(123) // [123]
 * ```
 */
export function shouldBeArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [ value ];
}

/**
 * Check whether a list is included in a challenge array.
 * - all=true: every element must be present
 * - all=false: at least one element must be present
 */
export function includes(list: any[] | any, challenge: any[], all: boolean = true): boolean {
  if (! list || ! (Array.isArray(list) ? list.length : 1)) {
    return true;
  }

  if (all) {
    return shouldBeArray(list).every((element) => challenge.includes(element));
  }

  return shouldBeArray(list).filter((element) => challenge.includes(element)).length > 0;
}

/**
 * Type guard that checks if a value is a plain object (not array, date, etc.).
 *
 * This function performs a strict check to determine if a value is a plain JavaScript object.
 * It excludes arrays, dates, functions, and other non-plain objects from being considered
 * as valid objects for object manipulation functions.
 *
 * @param object - The value to check
 * @returns True if the value is a plain object, false otherwise
 *
 * @example
 * ```typescript
 * isObject({}) // true
 * isObject({ a: 1 }) // true
 * isObject([]) // false (array)
 * isObject(new Date()) // false (date)
 * isObject(null) // false
 * isObject(undefined) // false
 * isObject('string') // false
 * isObject(123) // false
 * ```
 */
export function isObject(object: any): boolean {
  if (object === null || typeof object !== 'object') {
    return false;
  }

  return (object as Object).constructor.name === 'Object';
}

/**
 * Checks if a value is not a plain object or has no enumerable properties.
 *
 * This function combines object validation with emptiness checking, making it useful
 * for validating that an object exists and contains data before processing.
 *
 * @param object - The value to check
 * @returns True if the value is not a plain object or has no enumerable keys
 *
 * @example
 * ```typescript
 * isObjectEmpty({}) // true (empty object)
 * isObjectEmpty({ a: 1 }) // false (has properties)
 * isObjectEmpty([]) // true (not a plain object)
 * isObjectEmpty(null) // true (not a plain object)
 * isObjectEmpty(undefined) // true (not a plain object)
 * ```
 */
export function isObjectEmpty(object: any): boolean {
  return ! isObject(object) || Object.keys(object).length === 0;
}

/**
 * Performs deep equality comparison between two objects with flexible comparison options.
 *
 * This function provides comprehensive object comparison capabilities with several key features:
 * - Selective key comparison: Only compare specific keys if provided
 * - Strict vs loose equality: Choose between `===` and `==` comparison
 * - Extra key detection: Returns false if object2 has keys not in object1 (when no keys specified)
 * - Empty object handling: Properly handles empty objects and null/undefined values
 *
 * The function is particularly useful for:
 * - Form validation and change detection
 * - API request deduplication (as used in client.tsx)
 * - State comparison in React components
 * - Configuration validation
 *
 * @param object1 - The first object to compare (reference object)
 * @param object2 - The second object to compare against
 * @param keys - Optional array of specific keys to compare. If empty, compares all keys from object1
 * @param isStrict - Whether to use strict equality (===) or loose equality (==). Defaults to true
 * @returns true if objects are equal based on the specified criteria, false otherwise
 *
 * @example
 * ```typescript
 * // Basic equality comparison
 * isEqualObjects({ a: 1, b: 2 }, { a: 1, b: 2 }) // true
 *
 * // Partial key comparison
 * isEqualObjects({ a: 1, b: 2 }, { a: 1, b: 3 }, ['a']) // true (only 'a' compared)
 *
 * // Extra key detection
 * isEqualObjects({ a: 1 }, { a: 1, b: 3 }) // false (object2 has extra key 'b')
 *
 * // Loose vs strict comparison
 * isEqualObjects({ a: 1, b: '2' }, { a: 1, b: 2 }, [], false) // true (loose: '2' == 2)
 * isEqualObjects({ a: 1, b: '2' }, { a: 1, b: 2 }, [], true)  // false (strict: '2' !== 2)
 *
 * // Empty object handling
 * isEqualObjects({}, {}) // true
 * isEqualObjects(null, null) // true
 * isEqualObjects({}, null) // false
 * ```
 */
export function isEqualObjects(object1: SimpleObject<any>, object2: SimpleObject<any>, keys: string[] = [], isStrict: boolean = true): boolean {
  if (! isObject(object1) || ! isObject(object2)) {
    return false;
  }

  if (isObjectEmpty(object1) && isObjectEmpty(object2)) {
    return true;
  }

  const useAllKeys = keys.length === 0;
  const selectedKeys = useAllKeys ? Object.keys(object1) : keys;

  if (useAllKeys) {
    const obj2Keys = Object.keys(object2);

    if (obj2Keys.length !== selectedKeys.length) {
      return false;
    }
  }

  for (let i = 0; i < selectedKeys.length; i++) {
    const key = selectedKeys[i];

    const value1 = object1[key];
    const value2 = object2[key];

    if (! isEqual(value1, value2, isStrict)) {
      return false;
    }
  }

  return true;
}

/**
 * Compares two arrays for equality, checking if they contain the same elements
 * regardless of order (multiset comparison).
 *
 * This function performs a deep comparison of array elements using the `isEqual` function,
 * allowing for nested objects, arrays, and custom comparison logic. It checks if both arrays
 * contain the same elements with the same frequency, but order doesn't matter.
 *
 * @param array1 - First array to compare
 * @param array2 - Second array to compare
 * @param isStrict - Whether to use strict equality (===) or loose equality (==) for comparisons
 * @returns true if arrays contain the same elements (same frequency, any order), false otherwise
 *
 * @example
 * ```typescript
 * // Same elements, same order
 * isEqualArrays([1, 2, 3], [1, 2, 3]) // true
 *
 * // Same elements, different order
 * isEqualArrays([1, 2, 3], [3, 1, 2]) // true
 *
 * // Different elements
 * isEqualArrays([1, 2, 3], [1, 2, 4]) // false
 *
 * // Different frequencies
 * isEqualArrays([1, 2, 2], [1, 2, 3]) // false
 *
 * // Nested arrays
 * isEqualArrays([[1, 2], [3]], [[3], [1, 2]]) // true
 * ```
 */
export function isEqualArrays(array1: any[], array2: any[], isStrict: boolean = true): boolean {
  if (! Array.isArray(array1) || ! Array.isArray(array2)) {
    return false;
  }

  if (array1.length !== array2.length) {
    return false;
  }

  // Create a copy of array2 to avoid mutating the original
  const array2Copy = [...array2];

  // For each element in array1, find and remove a matching element from array2Copy
  for (let i = 0; i < array1.length; i++) {
    const value1 = array1[i];
    let found = false;

    for (let j = 0; j < array2Copy.length; j++) {
      const value2 = array2Copy[j];

      if (isEqual(value1, value2, isStrict)) {
        // Remove the matched element by splicing
        array2Copy.splice(j, 1);
        found = true;
        break;
      }
    }

    // If no match was found for this element, arrays are not equal
    if (! found) {
      return false;
    }
  }

  // If all elements were matched, array2Copy should be empty
  return array2Copy.length === 0;
}

/**
 * Returns the values from object1 that are different from object2.
 *
 * This function compares two objects and returns a new object containing only the
 * properties from object1 that differ from object2. It uses the same comparison
 * logic as isEqualObjects but returns the actual differing values instead of a boolean.
 *
 * @param object1 - The object to extract differences from
 * @param object2 - The reference object to compare against
 * @param isStrict - Whether to use strict equality (===) or loose equality (==)
 * @returns An object containing only the differing values from object1
 *
 * @example
 * ```typescript
 * const obj1 = { a: 1, b: 2, c: 3 };
 * const obj2 = { a: 1, b: 5, c: 3, d: 4 };
 *
 * objectDifference(obj1, obj2)
 * // Result: { b: 2 }
 * ```
 */
export function getObjectDifference(object1: SimpleObject<any>, object2: SimpleObject<any>, isStrict: boolean = true): SimpleObject<any> {
  if (! isObject(object1) || ! isObject(object2)) {
    return {};
  }

  const differences: SimpleObject<any> = {};

  for (const key in object1) {
    const value1 = object1[key];
    const value2 = object2[key] ?? undefined;

    if (value2 === undefined || ! isEqual(value1, value2, isStrict)) {
      differences[key] = value1;
    }
  }

  return differences;
}

/**
 * Creates a shallow copy of arrays and plain objects, returns the same value for others.
 *
 * This function provides a lightweight copying mechanism that only creates new
 * instances for arrays and plain objects, while returning primitive values and
 * other object types unchanged.
 *
 * @param object - Value to copy
 * @returns Shallow copy of arrays/objects, or the original value
 *
 * @example
 * ```typescript
 * copyObject([1, 2, 3]) // [1, 2, 3] (new array)
 * copyObject({ a: 1 }) // { a: 1 } (new object)
 * copyObject('string') // 'string' (same string)
 * copyObject(123) // 123 (same number)
 * ```
 */
export function copyObject<T>(object: T): T {
  if (isObject(object)) {
    return each({...object as SimpleObject<any>}, (value) => copyObject(value)) as T;
  }

  if (Array.isArray(object)) {
    return [ ...(object as any[]) ].map((value) => copyObject(value)) as unknown as T;
  }

  return object;
}

/**
 * Creates a new object containing only the specified keys from the original object.
 *
 * This function is useful for extracting specific properties from objects,
 * creating filtered versions, or preparing data for specific operations.
 * It's type-safe and preserves the original value types.
 *
 * @param object - Source object to filter
 * @param keys - Array of keys to include in the result
 * @returns New object with only the specified keys
 *
 * @example
 * ```typescript
 * const user = { id: 1, name: 'John', email: 'john@example.com', password: 'secret' };
 *
 * copyObjectKeys(user, ['name', 'email']) // { name: 'John', email: 'john@example.com' }
 * ```
 */
export function copyObjectKeys<T extends SimpleObject<any>, K extends keyof T>(object: T, keys: K[]): Pick<T, K> {
  const newObject = {} as Pick<T, K>;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (typeof object[key] === 'undefined') {
      continue;
    }

    newObject[key] = object[key];
  }

  return newObject;
}

/** Count items matching callback or value. */
export function countIf<T>(collection: T[], callback: ((value: T) => any) | any = true, strict: boolean = false): number {
  if (typeof callback !== 'function') {
    const challenge = callback;
    callback = (value: T) => strict ? value === challenge : (value as any) == challenge;
  }

  return collection.map(callback as (v: T) => any).filter(v => !!v).length;
}

/** Find object in list by key/value (== or === when strict). */
export function findObject<T extends SimpleObject<any>>(list: T[], key: string, value: any, strict: boolean = false): T | null {
  for (let i = 0; i < list.length; i++) {
    const item = list[i];

    if (typeof item[key] === 'undefined') {
      continue;
    }

    if (strict && item[key] === value) {
      return item;
    }

    if ((item as SimpleObject<any>)[key] == value) {
      return item;
    }
  }

  return null;
}

/** Length of array or count of values in object. */
export function len(object: any[] | SimpleObject<any>): number {
  if (Array.isArray(object)) {
    return object.length;
  }

  return Object.values(object).length;
}

/** Create an array [first..times) and map it. */
export function repeat<T>(times: number, callback: (value: number, index: number, array: number[]) => T, first: number = 0): T[] {
  const array = Array.from({ length: (first + times) - first }, (_, i) => first + i);
  return array.map(callback);
}

/**
 * Type-safe iteration and mapping function for objects and arrays.
 *
 * This is one of the most versatile functions in the module, providing a unified interface
 * for iterating over both objects and arrays while maintaining type safety through generics.
 * It can return either an array of mapped values or a mapped object, depending on the
 * `valuesOnly` parameter.
 *
 * Key features:
 * - Works with both objects and arrays seamlessly
 * - Type-safe with generic parameters (R defaults to T for convenience)
 * - Flexible return type: array or object based on `valuesOnly` flag
 * - Preserves original structure when `valuesOnly` is false
 * - Provides key, value, and index to the callback function
 *
 * @param object - The object or array to iterate over
 * @param callback - Function to execute for each item (value, key, index) => R
 * @param valuesOnly - If true, returns R[], otherwise returns SimpleObject<R>
 * @returns Either an array of mapped values or a mapped object
 *
 * @example
 * ```typescript
 * // Basic object mapping
 * each({ a: 1, b: 2 }, (value, key) => value * 2)
 * // Result: { a: 2, b: 4 }
 *
 * // Array mapping with values only
 * each([1, 2, 3], (value, key, index) => value * index, true)
 * // Result: [0, 2, 6]
 *
 * // Type-safe configuration processing
 * each<Config, ProcessedConfig>(configs, (config) => processConfig(config))
 *
 * // React component mapping
 * each<Item, ReactElement>(items, (item, key) => <ItemComponent key={key} item={item} />, true)
 * ```
 */
export function each<T, R = T>(object: SimpleObject<T> | T[], callback: (value: T, key: string, index: number) => R, valuesOnly: true): R[];
export function each<T, R = T>(object: SimpleObject<T> | T[], callback: (value: T, key: string, index: number) => R, valuesOnly?: false): SimpleObject<R>;

export function each<T, R = T>(object: SimpleObject<T> | T[], callback: (value: T, key: string, index: number) => R, valuesOnly: boolean = false): R[] | SimpleObject<R> {
  if (Array.isArray(object)) {

    const mapped = (object as T[]).map((value, index) => callback(value, String(index), index));
    if (valuesOnly) {
      return mapped;
    }

    const asObject: SimpleObject<R> = {};
    for (let i = 0; i < mapped.length; i++) {
      asObject[String(i)] = mapped[i];
    }

    return asObject;
  }

  const newObj: SimpleObject<R> = {};
  let index = 0;
  for (const key in object as SimpleObject<T>) {
    newObj[key] = callback((object as SimpleObject<T>)[key], key, index);
    index++;
  }

  if (valuesOnly) {
    return Object.values(newObj);
  }

  return newObj;
}

type EachKeyCallbackFuntion<T> = (key: string, index: number, object: SimpleObject<T>) => string;
type EachKeyCallbackValueFuntion<T, R> = (value: T, key: string, index: number, object: SimpleObject<T>) => R;

/** Map object keys (and optionally values), returning an array or mapped object. */
export function eachKey<T, R = T>(object: SimpleObject<T>, callback: EachKeyCallbackFuntion<T>, valuesOnly: true, callbackValue?: EachKeyCallbackValueFuntion<T, R>): R[];
export function eachKey<T, R = T>(object: SimpleObject<T>, callback: EachKeyCallbackFuntion<T>, valuesOnly?:  false, callbackValue?: EachKeyCallbackValueFuntion<T, R>): SimpleObject<R>;

export function eachKey<T, R = T>(object: SimpleObject<T>, callback: EachKeyCallbackFuntion<T>, valuesOnly?: boolean, callbackValue?: EachKeyCallbackValueFuntion<T, R>): R[] | SimpleObject<R> {
  const newObj: SimpleObject<R> = {};

  let index = 0;
  for (const key in object) {
    const newKey = callback(key, index, object);

    if (typeof callbackValue === 'function') {
      newObj[newKey] = callbackValue(object[key], key, index, object);
      continue;
    }

    newObj[newKey] = object[key] as any;
    index++;
  }

  if (valuesOnly) {
    return Object.values(newObj);
  }

  return newObj;
}

/** Unique values of an array. */
export function unique<T>(collection: T[]): T[] {
  return [ ...new Set(collection) ];
}

/** Keep or remove keys from an object. */
export function filterKey<T extends SimpleObject<any>>(object: T, keys: string | string[], keep: boolean = false): T {
  const list = shouldBeArray(keys);
  const newObj: T = {} as T;

  for (const key in object) {
    if (keep !== list.includes(key)) {
      continue;
    }

    (newObj as SimpleObject<any>)[key] = object[key];
  }

  return newObj;
}

/** Get option label by value from an options array. */
export function getLabelFromOptionsArray(array: any, key: string, empty: string = ''): string {
  const options = flattenOptionsArray(array);
  return options[key] || empty;
}

/**
 * Converts an array of option objects into a key-value mapping for efficient lookups.
 *
 * This function transforms a common pattern of option arrays (used in forms, selects, etc.)
 * into a more efficient lookup structure. It's particularly useful for:
 * - Converting form options to lookup tables
 * - Creating value-to-label mappings for display
 * - Optimizing option lookups in large datasets
 * - Preparing data for dropdown/select components
 *
 * The function is robust and handles various edge cases:
 * - Skips non-object items in the array
 * - Skips objects missing required `label` or `value` properties
 * - Converts all values and labels to strings for consistency
 * - Returns empty object for invalid inputs
 *
 * @param array - Array of option objects with `label` and `value` properties
 * @returns Object mapping option values to their labels
 *
 * @example
 * ```typescript
 * // Basic options conversion
 * flattenOptionsArray([
 *   { label: 'Option One', value: '1' },
 *   { label: 'Option Two', value: '2' }
 * ])
 * // Result: { '1': 'Option One', '2': 'Option Two' }
 *
 * // Handles mixed data types
 * flattenOptionsArray([
 *   { label: 'Active', value: 1 },
 *   { label: 'Inactive', value: 0 },
 *   null, // skipped
 *   { label: 'Pending' }, // skipped (missing value)
 *   { value: 'test' } // skipped (missing label)
 * ])
 * // Result: { '1': 'Active', '0': 'Inactive' }
 *
 * // Empty or invalid input
 * flattenOptionsArray([]) // {}
 * flattenOptionsArray(null) // {}
 * ```
 */
export function flattenOptionsArray(array: any): Record<string, string> {
  if (! Array.isArray(array)) {
    return {};
  }

  const options: Record<string, string> = {};
  for (let i = 0; i < array.length; i++) {
    const item = array[i];
    if (! item?.label || ! item?.value) {
      continue;
    }

    options[String(item.value)] = String(item.label);
  }

  return options;
}

/**
 * Converts an array of documents objects into a list of Ids mapping for efficient lookups.
 *
 * @param array - Array of documents objects or array of numbers.
 * @returns Array of numbers.
 *
 * @example
 * ```typescript
 * const users = [
 *   { id: 1, name: 'John' },
 *   { id: 2, name: 'Jane' },
 *   { id: 3, name: 'Jim' }
 * ];
 *
 * flattenDocumentsArray(users) // [1, 2, 3]
 * flattenDocumentsArray([1, 2, 3]) // [1, 2, 3]
 * flattenDocumentsArray(null) // []
 * flattenDocumentsArray(undefined) // []
 * flattenDocumentsArray([]) // []
 */
export function flattenDocumentIdsArray(array: ({ id: number } | number)[] | null | undefined): number[] {
  if (! array || ! Array.isArray(array) || array.length === 0) {
    return [];
  }

  return array.map(v => typeof v === 'number' ? v : (v as { id: number })?.id ?? null).filter((v) => v !== null) as number[];
}

/**
 * Extracts values for a specific key from an array of objects.
 *
 * This function efficiently plucks values from a specific property across all objects
 * in an array, filtering out null/undefined values. It's type-safe and preserves
 * the original value types from the objects.
 *
 * @param array - Array of objects to extract values from
 * @param key - The property key to extract from each object
 * @returns Array of non-null values for the specified key
 *
 * @example
 * ```typescript
 * const users = [
 *   { id: 1, name: 'John', age: 30 },
 *   { id: 2, name: 'Jane', age: 25 },
 *   { id: 3, name: null, age: 35 }
 * ];
 *
 * flattenObjectArrayByKey(users, 'name') // ['John', 'Jane']
 * flattenObjectArrayByKey(users, 'age') // [30, 25, 35]
 * flattenObjectArrayByKey(users, 'id') // [1, 2, 3]
 * ```
 */
export function flattenObjectArrayByKey<T extends SimpleObject<any>, K extends keyof T>(array: T[], key: K): Array<T[K]> {
  return array.map(v => (v[key] ?? null)).filter((v) => v !== null) as Array<T[K]>;
}

/**
 * Recursively searches for values by key throughout nested arrays and objects.
 *
 * This function performs a deep traversal of complex data structures to find
 * all values associated with a specific key, regardless of nesting depth.
 * It can optionally transform found values through a callback function.
 *
 * @param array - Array to search through (can contain nested objects/arrays)
 * @param key - The key to search for in all nested objects
 * @param callback - Optional function to transform found values
 * @returns Array of all found values (optionally transformed)
 *
 * @example
 * ```typescript
 * const data = [
 *   { id: 1, user: { name: 'John' } },
 *   { items: [{ id: 2, user: { name: 'Jane' } }] },
 *   { id: 3, user: null }
 * ];
 *
 * flattenObjectArrayByKeyRecursively(data, 'id') // [1, 2, 3]
 * flattenObjectArrayByKeyRecursively(data, 'name') // ['John', 'Jane']
 *
 * // With transformation callback
 * flattenObjectArrayByKeyRecursively(data, 'id', (value) => value * 2) // [2, 4, 6]
 * ```
 */
export function flattenObjectArrayByKeyRecursively(array: any[], key: string, callback: ((value: any, object: SimpleObject<any>) => any) | null = null): any[] {
  const results: any[] = [];

  if (typeof callback !== 'function') {
    callback = (value) => value;
  }

  const findValues = (value: any): void => {
    if (Array.isArray(value)) {
      value.forEach(findValues);
      return;
    }

    if (value !== null && typeof value === 'object') {
      for (const [entryKey, val] of Object.entries(value)) {
        if (entryKey === key) {
          results.push((callback as any)(val, value as SimpleObject<any>));
        }

        findValues(val);
      }
    }
  }

  findValues(array);
  return results;
}

/** Flatten list of objects using dot notation. */
export function flattenObjectList(list: SimpleObject<any>[]): SimpleObject<any>[] {
  return list.map(flattenObject);
}

/** Flatten a single object using dot notation. */
export function flattenObject(object: any): SimpleObject<any> {
  return flatten(object) as SimpleObject<any>;
}

/** First enumerable key or null. */
export function getObjectFirstKey(object: SimpleObject<any>): string | null {
  return Object.keys(object)[0] || null;
}

/** Highest numeric key (or -1 if none). */
export function getObjectLastNumericKey(object: SimpleObject<any>): number {
  const keys = Object.keys(object)
    .map((k) => parseStringToNumber(k))
    .filter((v) => v !== null) as number[];

  if (keys.length === 0) {
    return -1;
  }

  const maxKey = keys.length ? Math.max(...keys) : 0;
  return Math.max(maxKey, Object.values(object).length - 1);
}

/** Deep-merge objects/arrays recursively. */
export function mergeObjects(obj1: any, obj2: any): any {
  obj1 = copyObject(obj1);
  obj2 = copyObject(obj2);

  const isArray = Array.isArray(obj1);

  for (const key in obj1 as SimpleObject<any>) {
    const value = (obj1 as SimpleObject<any>)[key];

    if (! isObject(value) && ! Array.isArray(value)) {
      continue;
    }

    if (! isObject((obj2 as SimpleObject<any>)[key]) && ! Array.isArray((obj2 as SimpleObject<any>)[key])) {
      continue;
    }

    (obj2 as SimpleObject<any>)[key] = mergeObjects(value, (obj2 as SimpleObject<any>)[key] || {});
  }

  const response = Object.assign({}, obj1, obj2);
  return isArray ? createArrayFromObjectWithNumericKeys(response) : response;
}

/** Merge with preference for non-null values in the target. */
export function mergeObjectsOnNull(obj1: SimpleObject<any>, obj2: SimpleObject<any>): any {
  obj1 = copyObject(obj1);
  obj2 = copyObject(obj2);

  for (const key in obj2) {
    if (typeof obj1[key] !== 'undefined' && obj2[key] === null) {
      delete obj2[key];
    }
  }

  return mergeObjects(obj1, obj2);
}

/** Map object values to an array. */
export function mapToArray<T, R = T>(object: SimpleObject<T>, callback: (value: T, key: string) => R): R[] {
  return Object.keys(object).map((key) => callback(object[key], key));
}

/** Create array from object numeric keys, filling gaps with a value. */
export function createArrayFromObjectWithNumericKeys(object: SimpleObject<any>, emptyValue: any = null): any[] {
  if (isObjectEmpty(object)) {
    return [];
  }

  const size = getObjectLastNumericKey(object) + 1;
  const array = new Array(size);

  for (let i = 0; i < size; i++) {
    if (typeof (object as SimpleObject<any>)[i] === 'undefined') {
      array[i] = emptyValue;
      continue;
    }

    array[i] = (object as SimpleObject<any>)[i];
  }

  return array;
}

/**
 * Create an object from an array of keys.
 *
 * @param array - Array of keys.
 * @returns Object with keys as keys and values as keys.
 */
export function createObjectFromArrayOfKeys(array: string[]): SimpleObject<string> {
  return array.reduce((obj, item) => { obj[item] = item; return obj }, {});
}

/**
 * Reverse an object: key becomes value and value becomes key.
 *
 * @param object - Object to reverse.
 * @returns Reversed object.
 */
export function reverseObject(object: SimpleObject<string>): SimpleObject<string> {
  return Object.fromEntries(Object.entries(object).map(([key, value]) => [value, key]));
}

/**
 * Group an array of objects by a key.
 *
 * @param array - Array to group.
 * @param key - Key to group by.
 * @returns
 */
export function groupBy<T extends SimpleObject<any>>(array: T[], key: string): SimpleObject<T[]> {
  return array.reduce((obj, item) => {
    obj[item[key]] = obj[item[key]] || [];
    obj[item[key]].push(item);
    return obj;
  }, {});
}

/** Create object keyed by a property from an array of objects. */
export function createObjectFromArrayOfObjects<T extends SimpleObject<any>>(array: T[], key: string = 'id', value: string | ((item: T, id: any) => any) | null = null): Record<string, any> {
  const object: Record<string, any> = {};

  for (let i = 0; i < array.length; i++) {
    const item = array[i];
    if (typeof (item as SimpleObject<any>)[key] === 'undefined') {
      continue;
    }

    if (value && typeof value === 'function') {
      object[(item as SimpleObject<any>)[key]] = (value as (it: T, id: any) => any)(item, (item as SimpleObject<any>)[key]);
      continue;
    }

    if (value) {
      object[(item as SimpleObject<any>)[key]] = (item as SimpleObject<any>)[value];
      continue;
    }

    object[(item as SimpleObject<any>)[key]] = item;
  }

  return object;
}

/** Natural-sort simple arrays. */
export function natsortArray<T>(array: T[], options: NatsortOptions = { insensitive: true }): T[] {
  const sorter = natsort(options);
  return array.sort((a: any, b: any) => sorter(a || 0, b || 0));
}

/** Sort array of objects by a key or custom comparator. */
export function sortArrayOfObjects<T extends SimpleObject<any>>(array: T[], key: string, comparison: ((a: T, b: T) => number) | null = null, options: NatsortOptions = { insensitive: true }): T[] {
  if (comparison) {
    return array.sort(comparison)
  }

  const sorter = natsort(options);
  return array.sort((a, b) => sorter((a as SimpleObject<any>)[key] || 0, (b as SimpleObject<any>)[key] || 0));
}


